import type {
  Lesson,
  RepoState,
  QuizQuestion,
  GlossaryItem,
  CheatsheetItem,
} from "../../lib/types";

export const LESSON_REMOTE: Lesson = {
  courseId: "remote",
  courseTitle: "Remote vs local",
  id: "lesson-1",
  title: "Remote vs local",
  subtitle: "Lesson 3 of 5",
  steps: [
    {
      id: "intro",
      shortTitle: "The other half",
      shortSub: "Origin & your laptop",
      eyebrow: "Step 1 of 5",
      title: "There are two copies, not one.",
      sub: "Up to now you've only seen the local repo on your laptop. The other half lives on GitHub — that's the remote, almost always called origin. Everything you do on your laptop is private until you push it.",
      kind: "intro",
    },
    {
      id: "clone",
      shortTitle: "Clone the project",
      shortSub: "Copy origin → laptop",
      eyebrow: "Step 2 of 5",
      title: "Clone makes a local copy.",
      sub: "Right now your laptop is empty — the project only exists on GitHub. Click 'git clone' to copy it to your machine. You'll get the files and every commit, all at once.",
      kind: "sandbox",
      goal: "clone",
    },
    {
      id: "push",
      shortTitle: "Make a commit & push",
      shortSub: "Share your work",
      eyebrow: "Step 3 of 5",
      title: "Commit locally, then push.",
      sub: "Edit App.jsx, stage, and commit — same as last course. Notice your laptop is now one commit ahead of origin. To share it, push. The commit replicates to GitHub.",
      kind: "sandbox",
      goal: "push",
    },
    {
      id: "pull",
      shortTitle: "Pull a teammate's work",
      shortSub: "Fetch + fast-forward",
      eyebrow: "Step 4 of 5",
      title: "A teammate pushed. Now pull.",
      sub: "While you were working, a teammate pushed a new commit to origin. Your laptop is now behind. Run 'git fetch' to see it, then 'git pull' to bring it in.",
      kind: "sandbox",
      goal: "pull",
    },
    {
      id: "wrap",
      shortTitle: "Quiz & recap",
      shortSub: "Knowledge check",
      eyebrow: "Wrap-up",
      title: "Clone, push, pull. That's it.",
      sub: "Three commands cover 90% of remote work. Quick check below, then cheatsheet + glossary.",
      kind: "wrap",
    },
  ],
};


export const QUIZ_QUESTIONS_REMOTE: QuizQuestion[] = [
  {
    q: "You just made three commits locally. Who can see them?",
    options: [
      { t: "Everyone on the team — commits sync automatically", correct: false },
      { t: "Only you, until you push to origin", correct: true },
      { t: "Anyone with the repo's URL", correct: false },
      { t: "The reviewers you assigned", correct: false },
    ],
    why: "Right — committing only writes to your local repo. Nothing leaves your laptop until you push. This is the #1 surprise about git.",
    wrongWhy:
      "Commits are local-only until you push. Your laptop holds them privately until you explicitly send them to origin. Nothing is shared automatically.",
  },
  {
    q: "What does `origin` actually refer to?",
    options: [
      { t: "The first commit ever made in the repo", correct: false },
      { t: "The default name for the remote you cloned from", correct: true },
      { t: "The main branch on your laptop", correct: false },
      { t: "The user who created the repo", correct: false },
    ],
    why: "Exactly — origin is a nickname for the URL you cloned from. You can have multiple remotes, but the one you cloned from is named origin by default.",
    wrongWhy:
      "Origin is the default nickname for the remote URL you cloned from. It has nothing to do with the first commit or the project's author — it's a pointer to 'where I got this from.'",
  },
  {
    q: "What's the difference between `git fetch` and `git pull`?",
    options: [
      { t: "They do the same thing", correct: false },
      {
        t: "fetch downloads new commits from origin but doesn't change your branch; pull does fetch + fast-forward your branch",
        correct: true,
      },
      { t: "fetch is for branches, pull is for tags", correct: false },
      { t: "fetch is read-only, pull rewrites history", correct: false },
    ],
    why: "Yes — pull is just fetch + merge. Fetch lets you see what changed before deciding whether to bring it into your branch.",
    wrongWhy:
      "Fetch downloads new commits from origin without moving your branch — useful when you want to inspect first. Pull does the same fetch and then fast-forwards your branch to match.",
  },
];

export const GLOSSARY_REMOTE: GlossaryItem[] = [
  { term: "Remote", def: "Another copy of the repo, hosted somewhere else — usually on GitHub." },
  { term: "origin", def: "The default name for the remote you cloned from. You can rename it, but no one ever does." },
  { term: "Clone", def: "Download a full copy of a remote repo to your laptop, including all branches and history." },
  { term: "Push", def: "Send your local commits to a remote, updating that remote's branch tip to match yours." },
  { term: "Fetch", def: "Download new commits from a remote without changing any of your local branches." },
  { term: "Pull", def: "Fetch + fast-forward merge. The everyday way to bring teammates' work into your branch." },
  { term: "Tracking branch", def: "A local branch that 'follows' a specific remote branch — knows where to push and pull from." },
  { term: "Ahead / behind", def: "How many commits your local branch is in front of or behind its tracked remote branch." },
  { term: "Fast-forward", def: "A merge where your branch hadn't diverged, so git can just slide its tip forward to match the remote." },
];

export const CHEATSHEET_REMOTE: CheatsheetItem[] = [
  { cmd: "git clone <url>", desc: "Download a remote repo to your laptop for the first time." },
  { cmd: "git remote -v", desc: "List the remotes (URLs) configured for this repo." },
  { cmd: "git fetch", desc: "Download new commits from origin without touching your branches." },
  { cmd: "git pull", desc: "Fetch + fast-forward your current branch. The everyday sync." },
  { cmd: "git push", desc: "Send your local commits on the current branch to origin." },
  { cmd: "git push -u origin <branch>", desc: "Push for the first time and remember the upstream (so future pushes are just `git push`)." },
  { cmd: "git status", desc: "Shows ahead/behind status against the tracked remote." },
];

const APP_JSX = `import Header from './Header';

export default function App() {
  return (
    <div className="app">
      <Header />
      <main>
        <h1>Welcome to Atlas</h1>
        <p>Your Business Operating System.</p>
      </main>
    </div>
  );
}
`;

const HEADER_JSX = `export default function Header() {
  return (
    <header className="header">
      <img src="/logo.png" alt="logo" />
      <nav>
        <a href="/">Home</a>
        <a href="/about">About</a>
      </nav>
    </header>
  );
}
`;

const README_MD = `# Atlas

Internal business operating system.

Built by the team at Nymbl.
`;

/**
 * Initial state for the remote lesson.
 *
 *   Local: empty (no commits, no files, no branches).
 *   Origin: 3 commits, all on main, with the files above.
 *
 * Step 2 (clone) populates local from origin.
 * Step 3 (push) adds a local commit, then syncs origin.
 * Step 4 (pull) — see TEAMMATE_COMMIT below; the Lesson screen injects it
 *   onto origin when entering step 4 so the learner sees "1 behind".
 */
export const INITIAL_REPO_REMOTE: RepoState = {
  commits: [],
  branches: {},
  head: "main",
  files: {},
  remotes: {
    origin: {
      branches: { main: "c7d8e9f" },
      commits: [
        { id: "a1b2c3d", msg: "Initial commit", branch: "main", parent: null, x: 0 },
        { id: "e4f5a6b", msg: "Add Header component", branch: "main", parent: "a1b2c3d", x: 1 },
        { id: "c7d8e9f", msg: "Wire up homepage layout", branch: "main", parent: "e4f5a6b", x: 2 },
      ],
      files: {
        "App.jsx": APP_JSX,
        "Header.jsx": HEADER_JSX,
        "README.md": README_MD,
      },
    },
  },
  tracking: { main: { remote: "origin", branch: "main" } },
};

export const INITIAL_LOG_REMOTE = [
  { kind: "out" as const, text: "Project lives on GitHub at github.com/nymbl/atlas — your laptop has no copy yet." },
];

/**
 * The teammate commit that lands on origin when the learner enters step 4.
 * Parent (`x` and `parent`) is patched at injection time based on the current
 * origin tip so it slots in correctly behind whatever the learner pushed.
 */
export const TEAMMATE_COMMIT = {
  id: "9aabbcc",
  msg: "Tweak hero tagline",
  branch: "main" as const,
  authorIsTeammate: true,
};

/** README content after the teammate's commit lands. */
export const TEAMMATE_README = `# Atlas

Internal business operating system — your team's daily workspace.

Built by the team at Nymbl.
`;
